import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  useConfirm,
  useToast,
} from "../../../design-system";
import {
  closeMonth,
  listMonthClosures,
  reopenMonth,
} from "../../../helpers/kronaly_helper";
import useCurrentMembership from "../../../helpers/useCurrentMembership";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function labelForMonth(yyyymm) {
  const [y, m] = yyyymm.split("-").map((n) => parseInt(n, 10));
  return `${MONTHS_ES[m - 1]} ${y}`;
}

export function CierreMensualPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const membership = useCurrentMembership();
  const canAct = membership.isAdmin; // solo owner/admin pueden cerrar/reabrir

  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listMonthClosures({ months: 12 });
      setMonths(unwrap(r) || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudieron cargar los meses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClose = async (yearMonth, pendingCount) => {
    if (pendingCount > 0) {
      toast?.warning?.(
        "Hay correcciones pendientes",
        `Resuelve las ${pendingCount} correcciones pendientes antes de cerrar ${labelForMonth(yearMonth)}.`,
      );
      return;
    }
    const ok = await confirm?.({
      title: `Cerrar ${labelForMonth(yearMonth)}`,
      description: "Una vez cerrado no se podrán crear ni aprobar correcciones sobre jornadas de ese mes. Podrás reabrirlo después si hace falta.",
      confirmLabel: "Cerrar mes",
      tone: "warning",
    });
    if (!ok) return;
    try {
      await closeMonth(yearMonth);
      await load();
      toast?.success?.(`${labelForMonth(yearMonth)} cerrado`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "No se pudo cerrar el mes.";
      toast?.error?.("Error", msg);
    }
  };

  const handleReopen = async (yearMonth) => {
    const ok = await confirm?.({
      title: `Reabrir ${labelForMonth(yearMonth)}`,
      description: "Volverá a admitir correcciones. Recuerda cerrarlo de nuevo cuando termines.",
      confirmLabel: "Reabrir",
      tone: "default",
    });
    if (!ok) return;
    try {
      await reopenMonth(yearMonth);
      await load();
      toast?.success?.(`${labelForMonth(yearMonth)} reabierto`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "No se pudo reabrir el mes.";
      toast?.error?.("Error", msg);
    }
  };

  return (
    <Card>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
          Cierre mensual de jornadas
        </div>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4, lineHeight: 1.5 }}>
          Cuando un mes se cierra, sus jornadas quedan inmutables y no admiten correcciones.
          El cierre automático corre cada noche según los <strong>días de gracia</strong> configurados
          en <em>Configuración → Control horario</em>. Si hay correcciones pendientes, el cierre se salta
          hasta que se resuelvan.
        </div>
      </div>

      {error && <Alert tone="danger" style={{ marginBottom: 8 }}>{error}</Alert>}

      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
      ) : months.length === 0 ? (
        <EmptyState icon="Calendar" title="Sin datos" description="Sin meses para mostrar." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {months.map((m) => (
            <div key={m.year_month} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              border: "1px solid var(--kr-border-subtle)", borderRadius: 6,
              background: m.closed ? "var(--kr-neutral-50)" : "#fff",
            }}>
              <div style={{ minWidth: 140 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", textTransform: "capitalize" }}>
                  {labelForMonth(m.year_month)}
                </div>
                <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>
                  {m.year_month}
                </div>
              </div>
              <Badge tone={m.closed ? "neutral" : "success"} dot>
                {m.closed ? "Cerrado" : "Abierto"}
              </Badge>
              {m.pending_corrections > 0 && (
                <Badge tone="warning" dot>
                  {m.pending_corrections} pendiente{m.pending_corrections > 1 ? "s" : ""}
                </Badge>
              )}
              <div style={{ flex: 1 }} />
              {canAct && (
                m.closed ? (
                  <Button variant="ghost" size="sm" icon="Unlock" onClick={() => handleReopen(m.year_month)}>
                    Reabrir
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="Lock"
                    onClick={() => handleClose(m.year_month, m.pending_corrections)}
                    disabled={m.pending_corrections > 0}
                    title={m.pending_corrections > 0 ? "Resuelve las correcciones pendientes antes de cerrar." : ""}
                  >
                    Cerrar
                  </Button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default CierreMensualPanel;
